import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Star,
  BarChart3,
  Pencil,
  Trash2,
  Plus,
  Loader2,
  Store,
  Lock,
  Eye,
  EyeOff,
  Wand2,
  Image as ImageIcon,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import {
  listRestaurants,
  createRestaurant,
  updateRestaurant,
  deleteRestaurant,
  uploadLogo,
  deleteLogo,
  formatApiError,
} from "@/api";
import { RestaurantAccess } from "@/components/RestaurantAccess";
import { LogoutButton } from "@/components/LogoutButton";
import { NfcSupports } from "@/components/NfcSupports";

const slugifyClient = (text) =>
  text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const formatDate = (iso) =>
  new Intl.DateTimeFormat("es-ES", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(iso));

const inputClass =
  "w-full rounded-xl border border-[#E7E2D7] bg-white/90 px-4 py-3 text-sm text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:ring-2 focus:ring-[#C25E38]/30 focus:border-[#C25E38]/50 transition-shadow duration-200";

const labelClass =
  "font-mono-label text-[10px] uppercase tracking-[0.25em] text-[#A8A29E] px-1";

const generatePassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!-";
  const values = new Uint32Array(14);
  crypto.getRandomValues(values);
  return Array.from(values, (n) => chars[n % chars.length]).join("");
};

const isHex = (v) => /^#[0-9A-Fa-f]{6}$/.test(v);

const ColorField = ({ label, value, onChange, defaultColor, testId }) => (
  <div className="flex flex-col gap-1.5">
    <label className={labelClass}>{label}</label>
    <div className="flex items-center gap-3">
      <input
        type="color"
        value={isHex(value) ? value : defaultColor}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`${testId}-picker`}
        aria-label={`${label} (selector)`}
        className="h-11 w-14 shrink-0 rounded-lg border border-[#E7E2D7] bg-white cursor-pointer p-1"
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value.trim())}
        placeholder={`${defaultColor} (por defecto)`}
        data-testid={`${testId}-hex`}
        maxLength={7}
        className={`${inputClass} font-mono-label`}
      />
    </div>
  </div>
);

export default function SuperAdmin() {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [slug, setSlug] = useState("");
  const [googleUrl, setGoogleUrl] = useState("");
  const [notifEmail, setNotifEmail] = useState("");
  const [colorPrimary, setColorPrimary] = useState("");
  const [colorSecondary, setColorSecondary] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoRemoved, setLogoRemoved] = useState(false);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerPassword, setOwnerPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const fileInputRef = useRef(null);

  const load = () =>
    listRestaurants()
      .then(setRestaurants)
      .catch(() => toast.error("No se han podido cargar los restaurantes."))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setEditing(null);
    setName("");
    setDescription("");
    setSlug("");
    setGoogleUrl("");
    setNotifEmail("");
    setColorPrimary("");
    setColorSecondary("");
    setLogoFile(null);
    setLogoPreview(null);
    setLogoRemoved(false);
    setOwnerEmail("");
    setOwnerPassword("");
    setShowPassword(false);
    setSlugTouched(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const startEdit = (r) => {
    setEditing(r.slug);
    setName(r.name);
    setDescription(r.description || "");
    setSlug(r.slug);
    setGoogleUrl(r.google_review_url);
    setNotifEmail(r.notification_email || "");
    setColorPrimary(r.color_primary || "");
    setColorSecondary(r.color_secondary || "");
    setLogoFile(null);
    setLogoPreview(r.logo_url || null);
    setLogoRemoved(false);
    setOwnerEmail(r.owner_email || "");
    setOwnerPassword("");
    setShowPassword(false);
    setSlugTouched(true);
    if (fileInputRef.current) fileInputRef.current.value = "";
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      toast.error("Formato no válido. Usa PNG, JPG o WEBP.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("El logo no puede superar 2 MB.");
      return;
    }
    setLogoFile(file);
    setLogoRemoved(false);
    const reader = new FileReader();
    reader.onload = () => setLogoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setLogoRemoved(true);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleGeneratePassword = () => {
    setOwnerPassword(generatePassword());
    setShowPassword(true);
    toast.success("Contraseña generada. Anótala ahora: no se volverá a mostrar.");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!editing && ownerPassword.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (editing && ownerPassword && ownerPassword.length < 8) {
      toast.error("La nueva contraseña debe tener al menos 8 caracteres.");
      return;
    }
    for (const [label, value] of [["principal", colorPrimary], ["secundario", colorSecondary]]) {
      if (value && !isHex(value)) {
        toast.error(`El color ${label} debe ser HEX válido (#RRGGBB).`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        name,
        slug,
        google_review_url: googleUrl,
        description: description.trim() || null,
        color_primary: colorPrimary || null,
        color_secondary: colorSecondary || null,
      };
      const saved = editing
        ? await updateRestaurant(editing, {
            ...payload,
            owner_email: ownerEmail || undefined,
            new_password: ownerPassword || undefined,
            notification_email: notifEmail.trim() || undefined,
          })
        : await createRestaurant({
            ...payload,
            owner_email: ownerEmail,
            owner_password: ownerPassword,
            notification_email: notifEmail.trim() || null,
          });
      const finalSlug = saved.slug;
      try {
        if (logoFile) await uploadLogo(finalSlug, logoFile);
        else if (editing && logoRemoved) await deleteLogo(finalSlug);
      } catch (logoErr) {
        toast.error(formatApiError(logoErr, "Restaurante guardado, pero el logo no se pudo subir."));
      }
      toast.success(editing ? "Restaurante actualizado." : "Restaurante y acceso privado creados.");
      resetForm();
      load();
    } catch (err) {
      toast.error(formatApiError(err, "No se ha podido guardar. Revisa los datos."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (s) => {
    try {
      await deleteRestaurant(s);
      toast.success("Restaurante eliminado.");
      setConfirmDelete(null);
      if (editing === s) resetForm();
      load();
    } catch {
      toast.error("No se ha podido eliminar.");
    }
  };

  return (
    <div className="relative min-h-screen bg-[#FDFBF7] text-[#1C1917]">
      <div className="ambient-glow" />
      <div className="grain-overlay" />

      <div
        className="relative z-10 max-w-2xl mx-auto px-5 py-8 sm:py-12 flex flex-col gap-10"
        data-testid="superadmin-page"
      >
        <motion.header
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="space-y-3"
        >
          <div className="flex items-center justify-between gap-4">
            <p className="font-mono-label text-[10px] uppercase tracking-[0.3em] text-[#B38D46]">
              Review QR · Administración interna
            </p>
            <LogoutButton />
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-medium tracking-tight leading-[1.1]">
            Gestión de <em className="text-[#C25E38]">restaurantes</em>
          </h1>
        </motion.header>

        <motion.form
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.08, ease: "easeOut" }}
          onSubmit={handleSubmit}
          data-testid="restaurant-form"
          className="bg-white border border-[#E7E2D7] rounded-2xl p-5 sm:p-7 shadow-[0_10px_30px_-10px_rgba(28,25,23,0.06)] flex flex-col gap-4"
        >
          <h2 className="font-serif-display text-xl font-medium text-[#1C1917]">
            {editing ? "Editar restaurante" : "Nuevo restaurante"}
          </h2>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="input-name" className={labelClass}>
              Nombre del restaurante
            </label>
            <input
              id="input-name"
              data-testid="input-restaurant-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (!slugTouched) setSlug(slugifyClient(e.target.value));
              }}
              placeholder="Ej. Casa Mar"
              required
              className={inputClass}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="input-description" className={labelClass}>
              Descripción / tipo de cocina
            </label>
            <input
              id="input-description"
              data-testid="input-restaurant-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ej. Cocina mediterránea"
              maxLength={120}
              className={inputClass}
            />
            <p className="text-xs text-[#A8A29E] px-1">
              Si está vacía, no se muestra ningún texto.
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="input-slug" className={labelClass}>
              Slug
            </label>
            <input
              id="input-slug"
              data-testid="input-restaurant-slug"
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(slugifyClient(e.target.value));
              }}
              placeholder="casa-mar"
              required
              className={`${inputClass} font-mono-label`}
            />
            <p className="text-xs text-[#A8A29E] px-1">
              {slug
                ? `Página del cliente: /review/${slug} · Panel: /admin/${slug}`
                : "Se genera automáticamente a partir del nombre."}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ColorField
              label="Color principal"
              value={colorPrimary}
              onChange={setColorPrimary}
              defaultColor="#1C1917"
              testId="color-primary"
            />
            <ColorField
              label="Color secundario"
              value={colorSecondary}
              onChange={setColorSecondary}
              defaultColor="#C25E38"
              testId="color-secondary"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className={labelClass}>Logo</label>
            <div className="flex items-center gap-4">
              {logoPreview ? (
                <img
                  src={logoPreview}
                  alt="Vista previa del logo"
                  data-testid="logo-preview"
                  className="h-16 w-16 rounded-2xl object-cover border border-[#E7E2D7] bg-white"
                />
              ) : (
                <div
                  data-testid="logo-placeholder"
                  className="h-16 w-16 rounded-2xl border border-dashed border-[#E7E2D7] bg-white/60 flex items-center justify-center"
                >
                  <ImageIcon className="h-5 w-5 text-[#A8A29E]" />
                </div>
              )}
              <div className="flex flex-col items-start gap-2">
                <button
                  type="button"
                  data-testid="upload-logo-button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-xs font-medium rounded-full px-3.5 py-2 border border-[#E7E2D7] text-[#57534E] hover:border-[#C25E38]/40 hover:text-[#C25E38] transition-colors duration-200 cursor-pointer bg-white/70"
                >
                  {logoPreview ? "Cambiar logo" : "Subir logo"}
                </button>
                {logoPreview && (
                  <button
                    type="button"
                    data-testid="remove-logo-button"
                    onClick={handleRemoveLogo}
                    className="text-xs font-medium text-[#A8A29E] hover:text-[#C25E38] transition-colors duration-200 cursor-pointer"
                  >
                    Eliminar logo
                  </button>
                )}
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              data-testid="input-logo-file"
              onChange={handleLogoSelect}
            />
            <p className="text-xs text-[#A8A29E] px-1">
              PNG, JPG o WEBP · máx. 2 MB · idealmente cuadrado o con transparencia.
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="input-google-url" className={labelClass}>
              Google Review URL
            </label>
            <input
              id="input-google-url"
              data-testid="input-restaurant-google-url"
              type="url"
              value={googleUrl}
              onChange={(e) => setGoogleUrl(e.target.value)}
              placeholder="https://g.page/tu-restaurante/review"
              required
              className={inputClass}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="input-notif-email" className={labelClass}>
              Email de notificaciones
            </label>
            <input
              id="input-notif-email"
              data-testid="input-notification-email"
              type="email"
              value={notifEmail}
              onChange={(e) => setNotifEmail(e.target.value)}
              placeholder="encargado@restaurante.com (opcional)"
              className={inputClass}
            />
            <p className="text-xs text-[#A8A29E] px-1">
              Aquí llegan los avisos de nuevas valoraciones. Independiente del email de acceso.
            </p>
          </div>

          <div className="border-t border-[#E7E2D7] pt-4 flex flex-col gap-4" data-testid="owner-credentials-section">
            <h3 className="font-serif-display text-base font-medium text-[#1C1917] flex items-center gap-2">
              <Lock className="h-4 w-4 text-[#C25E38]" /> Acceso privado del restaurante
            </h3>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="input-owner-email" className={labelClass}>
                Email de acceso
              </label>
              <input
                id="input-owner-email"
                data-testid="input-owner-email"
                type="email"
                value={ownerEmail}
                onChange={(e) => setOwnerEmail(e.target.value)}
                placeholder="gerencia@restaurante.com"
                required
                className={inputClass}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="input-owner-password" className={labelClass}>
                {editing ? "Establecer nueva contraseña" : "Contraseña"}
              </label>
              <div className="relative">
                <input
                  id="input-owner-password"
                  data-testid="input-owner-password"
                  type={showPassword ? "text" : "password"}
                  value={ownerPassword}
                  onChange={(e) => setOwnerPassword(e.target.value)}
                  placeholder={editing ? "Dejar en blanco para mantener la actual" : "Mínimo 8 caracteres"}
                  required={!editing}
                  minLength={editing ? undefined : 8}
                  autoComplete="new-password"
                  className={`${inputClass} pr-12`}
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A8A29E] hover:text-[#C25E38] transition-colors duration-200 cursor-pointer"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="flex items-center justify-between gap-3 px-1">
                <p className="text-xs text-[#A8A29E]">
                  {editing
                    ? "La contraseña actual nunca se muestra."
                    : "Se guardará cifrada; nunca en texto plano."}
                </p>
                <button
                  type="button"
                  data-testid="generate-password-button"
                  onClick={handleGeneratePassword}
                  className="flex items-center gap-1.5 text-xs font-medium text-[#B38D46] hover:text-[#C25E38] transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  <Wand2 className="h-3.5 w-3.5" /> Generar contraseña segura
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 pt-1">
            <button
              type="submit"
              data-testid="save-restaurant-button"
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 bg-[#1C1917] hover:bg-[#C25E38] disabled:bg-[#E7E5E4] disabled:text-[#A8A29E] text-[#FDFBF7] font-medium text-sm rounded-xl px-5 py-3.5 transition-colors duration-300 cursor-pointer"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              {editing ? "Guardar cambios" : "Crear restaurante"}
            </button>
            {editing && (
              <>
                <a
                  href={`/review/${slug || editing}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="preview-restaurant-button"
                  className="flex items-center gap-1.5 px-5 py-3.5 rounded-xl border border-[#E7E2D7] text-sm text-[#57534E] hover:border-[#C25E38]/40 hover:text-[#C25E38] transition-colors duration-200"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Vista previa
                </a>
                <button
                  type="button"
                  data-testid="cancel-edit-button"
                  onClick={resetForm}
                  className="px-5 py-3.5 rounded-xl border border-[#E7E2D7] text-sm text-[#57534E] hover:border-[#C25E38]/40 transition-colors duration-200 cursor-pointer"
                >
                  Cancelar
                </button>
              </>
            )}
          </div>
        </motion.form>

        <motion.section
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.16, ease: "easeOut" }}
          className="flex flex-col gap-4"
        >
          <h2 className="font-serif-display text-xl sm:text-2xl font-medium text-[#1C1917]">
            Restaurantes
          </h2>

          {loading ? (
            <p
              data-testid="restaurants-loading"
              className="text-sm text-[#A8A29E] font-mono-label uppercase tracking-[0.25em] animate-pulse"
            >
              Cargando...
            </p>
          ) : restaurants.length === 0 ? (
            <div
              data-testid="restaurants-empty"
              className="bg-white/60 border border-dashed border-[#E7E2D7] rounded-2xl p-10 flex flex-col items-center gap-3 text-center"
            >
              <Store className="h-6 w-6 text-[#A8A29E]" />
              <p className="text-sm text-[#A8A29E]">No hay restaurantes todavía.</p>
            </div>
          ) : (
            <ul className="flex flex-col gap-3" data-testid="restaurants-list">
              {restaurants.map((r, i) => (
                <motion.li
                  key={r.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: Math.min(i * 0.05, 0.4) }}
                  data-testid={`restaurant-row-${r.slug}`}
                  className="bg-white border border-[#E7E2D7] rounded-2xl px-5 py-4 shadow-[0_10px_30px_-10px_rgba(28,25,23,0.06)] flex flex-col gap-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      {r.logo_url ? (
                        <img
                          src={r.logo_url}
                          alt={`Logo de ${r.name}`}
                          data-testid={`row-logo-${r.slug}`}
                          className="h-10 w-10 rounded-xl object-cover border border-[#E7E2D7] bg-white shrink-0"
                        />
                      ) : null}
                      <div className="min-w-0">
                        <p className="font-serif-display text-lg font-medium text-[#1C1917] leading-snug">
                          {r.name}
                        </p>
                        <p className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-[#B38D46] mt-1">
                          {r.slug}
                        </p>
                      </div>
                    </div>
                    <span className="font-mono-label text-[10px] uppercase tracking-[0.15em] text-[#A8A29E] whitespace-nowrap pt-1">
                      {formatDate(r.created_at)}
                    </span>
                  </div>

                  {r.description && (
                    <p className="text-xs text-[#A8A29E]" data-testid={`row-description-${r.slug}`}>
                      {r.description}
                    </p>
                  )}

                  <p
                    className="text-xs text-[#57534E] truncate"
                    data-testid={`restaurant-google-url-${r.slug}`}
                  >
                    {r.google_review_url}
                  </p>

                  <p
                    className="text-xs text-[#57534E]"
                    data-testid={`owner-email-${r.slug}`}
                  >
                    Propietario:{" "}
                    <span className="font-mono-label">{r.owner_email || "—"}</span>
                  </p>

                  <p
                    className="text-xs text-[#57534E]"
                    data-testid={`notif-email-${r.slug}`}
                  >
                    Notificaciones:{" "}
                    <span className="font-mono-label">{r.notification_email || "—"}</span>
                  </p>

                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <a
                      href={`/review/${r.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`open-review-${r.slug}`}
                      className="flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border border-[#E7E2D7] text-[#57534E] hover:border-[#C25E38]/40 hover:text-[#C25E38] transition-colors duration-200"
                    >
                      <Star className="h-3 w-3" /> Valoración
                    </a>
                    <a
                      href={`/admin/${r.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`open-admin-${r.slug}`}
                      className="flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border border-[#E7E2D7] text-[#57534E] hover:border-[#C25E38]/40 hover:text-[#C25E38] transition-colors duration-200"
                    >
                      <BarChart3 className="h-3 w-3" /> Panel
                    </a>
                    <span className="flex-1" />
                    {confirmDelete === r.slug ? (
                      <>
                        <button
                          type="button"
                          data-testid={`confirm-delete-${r.slug}`}
                          onClick={() => handleDelete(r.slug)}
                          className="flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 bg-[#C25E38] text-[#FDFBF7] hover:bg-[#A84C2A] transition-colors duration-200 cursor-pointer"
                        >
                          <Trash2 className="h-3 w-3" /> Confirmar
                        </button>
                        <button
                          type="button"
                          data-testid={`cancel-delete-${r.slug}`}
                          onClick={() => setConfirmDelete(null)}
                          className="text-xs font-medium rounded-full px-3 py-1.5 border border-[#E7E2D7] text-[#57534E] transition-colors duration-200 cursor-pointer"
                        >
                          Cancelar
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          data-testid={`edit-restaurant-${r.slug}`}
                          onClick={() => startEdit(r)}
                          aria-label={`Editar ${r.name}`}
                          className="p-2 rounded-full border border-[#E7E2D7] text-[#57534E] hover:border-[#C25E38]/40 hover:text-[#C25E38] transition-colors duration-200 cursor-pointer"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          data-testid={`delete-restaurant-${r.slug}`}
                          onClick={() => setConfirmDelete(r.slug)}
                          aria-label={`Eliminar ${r.name}`}
                          className="p-2 rounded-full border border-[#E7E2D7] text-[#A8A29E] hover:border-[#C25E38]/40 hover:text-[#C25E38] transition-colors duration-200 cursor-pointer"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </div>

                  <RestaurantAccess restaurant={r} />
                </motion.li>
              ))}
            </ul>
          )}
        </motion.section>

        <NfcSupports restaurants={restaurants} />

        <footer className="text-center pb-2">
          <p className="font-mono-label text-[9px] uppercase tracking-[0.25em] text-[#A8A29E]">
            Review QR · Uso interno
          </p>
        </footer>
      </div>
    </div>
  );
}
